import {
  Activity,
  Pause,
  Play,
  Trash2,
  Radio,
  Thermometer,
  AlertTriangle,
  Calendar,
  Cpu,
  Server,
  Zap,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeviceNames } from '../hooks/useDeviceNames';
import {
  useNatsActivity,
  NatsEvent,
  getCategoryLabel,
  getCategoryColor,
  formatSource,
  formatEventMessage,
  opiszZrodlo,
} from '../hooks/useNatsActivity';
import { describeConnection } from '../lib/wsConnectionStatus';
import { useAuth } from '../providers/AuthProvider';

// Icon mapping for categories
const categoryIcons: Record<NatsEvent['category'], typeof Activity> = {
  telemetry: Thermometer,
  alerts: AlertTriangle,
  events: Calendar,
  modules: Cpu,
  system: Server,
  unknown: Zap,
};

interface NatsActivityFeedProps {
  maxHeight?: string;
  showHeader?: boolean;
  compact?: boolean;
}

export function NatsActivityFeed({
  maxHeight = '400px',
  showHeader = true,
  compact = false,
}: NatsActivityFeedProps) {
  const { t } = useTranslation();

  // Use real NATS events — no demo fallback
  const {
    events,
    isConnected,
    connectionState,
    reconnectAttempt,
    retryInSeconds,
    reconnectNow,
    isPaused,
    clearEvents,
    togglePause,
  } = useNatsActivity();

  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [filter, setFilter] = useState<NatsEvent['category'] | 'all'>('all');

  // Nazwy czujników z kartoteki (ta sama migawka, co ekran „Aplikacje
  // i urządzenia"). Komunikat składa się PRZY RENDERZE, bo nazwy przychodzą
  // chwilę po pierwszych zdarzeniach — `event.message` z hooka to wersja bez nazw.
  const { currentOrganization } = useAuth();
  const { nazwaUrzadzenia } = useDeviceNames(currentOrganization?.organization_id);
  const kontekst = { nazwaUrzadzenia };
  const opisz = (event: NatsEvent) => {
    const parsed = {
      category: event.category,
      source: event.source,
      action: event.action,
      tenantId: event.tenantId,
      deviceId: event.deviceId,
      signal: event.signal,
    };
    return {
      tytul: formatEventMessage(parsed, event.data, kontekst).message,
      zrodlo: opiszZrodlo(parsed, kontekst),
      plakietka: event.deviceId
        ? (nazwaUrzadzenia(event.deviceId) ?? event.deviceId)
        : formatSource(event.source),
    };
  };

  const status = describeConnection(connectionState, reconnectAttempt, retryInSeconds);

  const filteredEvents = filter === 'all' ? events : events.filter((e) => e.category === filter);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatTimeDiff = (date: Date) => {
    const diff = Date.now() - date.getTime();
    if (diff < 1000) return 'teraz';
    if (diff < 60000) return `${Math.floor(diff / 1000)}s temu`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m temu`;
    return formatTime(date);
  };

  // Update time display every second
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="card overflow-hidden">
      {/* Animacja wejścia nowego zdarzenia live-feedu: krótki brand-flash tła +
          zsunięcie z góry. prefers-reduced-motion obsługuje globalna reguła
          w aurora/tokens.css (animation-duration:.001ms !important). */}
      <style>{`
        @keyframes nats-in{from{opacity:0;transform:translateY(-6px);background:rgba(var(--ids-brand-rgb),.10)}}
        .nats-item-in{animation:nats-in .4s var(--ids-ease-decelerate) both}
      `}</style>
      {showHeader && (
        <div className="card__head justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-aurora-brand" />
              <h2 className="card__title">{t('dashboard.systemActivity', 'Aktywność systemu')}</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${status.dotClass}`} />
              <span className="text-xs text-aurora-subtle">{status.label}</span>
              {status.canRetry && (
                <button
                  onClick={reconnectNow}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
                  title="Połącz teraz"
                >
                  <RefreshCw className="w-3 h-3" />
                  Połącz teraz
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Category filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as NatsEvent['category'] | 'all')}
              className="select max-w-[160px]"
            >
              <option value="all">Wszystkie</option>
              <option value="telemetry">Telemetria</option>
              <option value="alerts">Alerty</option>
              <option value="events">Zdarzenia</option>
              <option value="modules">Moduły</option>
              <option value="system">System</option>
            </select>

            {/* Controls — .iconbtn DS; stan wstrzymania sygnalizowany tintem
                tokenowym success zamiast surowej palety (warianty hover:
                utrzymują zieleń nad .iconbtn:hover z components.css). */}
            <button
              onClick={togglePause}
              className={`iconbtn ${isPaused ? 'bg-aurora-success/15 text-aurora-success hover:bg-aurora-success/25 hover:text-aurora-success' : ''}`}
              title={isPaused ? 'Wznów' : 'Wstrzymaj'}
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <button onClick={clearEvents} className="iconbtn" title="Wyczyść">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Events list */}
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Radio className="w-12 h-12 text-aurora-subtle mb-3" />
            <p className="text-aurora-muted mb-1">
              {isConnected ? 'Brak zdarzeń NATS' : 'Brak połączenia z NATS'}
            </p>
            <p className="text-sm text-aurora-subtle">
              {isConnected
                ? 'Oczekiwanie na komunikaty...'
                : connectionState === 'unconfigured'
                  ? 'Adres bramy realtime nie jest ustawiony w tej instalacji.'
                  : `${status.label}. Zdarzenia pojawią się automatycznie po nawiązaniu połączenia.`}
            </p>
            {!isConnected && status.canRetry && (
              <button
                onClick={reconnectNow}
                className="mt-4 flex items-center gap-2 px-3 py-1.5 rounded-ids-md bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Połącz teraz
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-aurora-border">
            {filteredEvents.map((event) => {
              const colors = getCategoryColor(event.category);
              const Icon = categoryIcons[event.category];
              const isExpanded = expandedEvent === event.id;
              const { tytul, zrodlo, plakietka } = opisz(event);

              return (
                <div
                  key={event.id}
                  className={`nats-item-in px-4 py-3 hover:bg-aurora-bg-subtle transition-colors cursor-pointer ${
                    compact ? 'py-2' : ''
                  }`}
                  onClick={() => setExpandedEvent(isExpanded ? null : event.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Category icon */}
                    <div className={`p-2 rounded-ids-md ${colors.bg} shrink-0`}>
                      <Icon className={`w-4 h-4 ${colors.text}`} />
                    </div>

                    {/* Event info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {/* Source badge */}
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}
                        >
                          {plakietka}
                        </span>
                        {/* Category badge */}
                        <span className="badge">{getCategoryLabel(event.category)}</span>
                      </div>
                      <p className="text-sm text-aurora-text font-medium">{tytul}</p>
                      {/* Zdanie o źródle zamiast surowego tematu NATS — temat
                          jest w rozwinięciu, obok ładunku. */}
                      {!compact && (
                        <p className="text-xs text-aurora-subtle truncate mt-0.5">{zrodlo}</p>
                      )}
                    </div>

                    {/* Time & expand */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-aurora-muted mono">
                          {formatTimeDiff(event.timestamp)}
                        </p>
                        {!compact && (
                          <p className="text-xs text-aurora-subtle mono">
                            {formatTime(event.timestamp)}
                          </p>
                        )}
                      </div>
                      {!compact && (
                        <button className="p-1 text-aurora-subtle hover:text-aurora-muted">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded data view */}
                  {isExpanded && !compact && (
                    <div className="mt-3 ml-11">
                      <div className="p-3 bg-aurora-bg border border-aurora-border rounded-ids-md">
                        <p className="text-xs text-aurora-subtle mb-1">
                          Temat NATS:{' '}
                          <span className="font-mono text-aurora-muted break-all">
                            {event.subject}
                          </span>
                        </p>
                        <p className="text-xs text-aurora-subtle mb-2 font-mono">Ładunek:</p>
                        <pre className="text-xs text-aurora-muted font-mono overflow-x-auto">
                          {JSON.stringify(event.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer stats */}
      {!compact && (
        <div className="px-4 py-2 border-t border-aurora-border bg-aurora-bg-subtle">
          <div className="flex items-center justify-between text-xs text-aurora-subtle">
            <span>{filteredEvents.length} zdarzeń</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {isPaused ? 'Wstrzymano' : 'Na żywo'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact version for sidebar or small areas
export function NatsActivityCompact() {
  return <NatsActivityFeed maxHeight="250px" showHeader={false} compact />;
}
